import React, {Suspense, lazy, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ChannelLogo} from '../../channels/ChannelLogo';
import {Target} from '../../icons';
import {choosesGoogleConversion, googleActionsFor} from '../conversionSuggestion';

// El selector de eventos del Panel es grande y sólo hace falta si alguien aprieta «Cambiar»: baja
// recién entonces.
const GoogleConversionModal = lazy(() => import('./GoogleConversionModal'));

/**
 * Qué conversión va a seguir cada cuenta de Google del cliente, y cambiarla (C-01).
 *
 * Pablo, en el recorrido del 13/10: «me estás monitoreando la conversión: ponémela ahí y permitime
 * corregirla desde ahí», y «la mayoría de las veces, todas las conversiones primarias es un error».
 * Google nacía con todas las primarias y la pantalla no lo decía. Ahora la fila lo dice y «Cambiar»
 * abre el mismo selector de eventos del Panel (`CategorizedActionsSelect`). Lo elegido viaja en el
 * `selected_actions` del alta (`wizardPayload`).
 *
 * Dibuja las mismas líneas que el selector de Meta (`ConversionPicker`), para que la conversión de
 * cada cuenta se lea igual en las dos plataformas.
 */
export function GoogleConversionPicker({group, conversionEdits, dispatch, accountActions, loadAccountActions,
                                        showAccountName}) {
    const googleAccounts = group.accounts.filter(entry => choosesGoogleConversion(entry.account));
    if (!googleAccounts.length) return null;
    const namesTheAccount = showAccountName === undefined ? group.accounts.length > 1 : showAccountName;

    return (
        <div className="ob-conversion -google">
            {googleAccounts.map(entry => (
                <GoogleConversionLine key={entry.pk} entry={entry} showAccountName={namesTheAccount}
                                      chosen={googleActionsFor(entry.pk, conversionEdits)}
                                      actions={accountActions?.[String(entry.pk)]}
                                      loadAccountActions={loadAccountActions} dispatch={dispatch}/>
            ))}
        </div>
    );
}

function GoogleConversionLine({entry, showAccountName, chosen, actions, loadAccountActions, dispatch}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const accountName = entry.account.name || entry.account.account_id;

    return (
        <div className="ob-conversion__line">
            <span className="ob-conversion__label">
                <Target size={13}/>
                {showAccountName && (
                    <ChannelLogo channel={entry.account.channel} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                )}
                <span className="ob-conversion__label-text">
                    {showAccountName
                        ? t('onboarding.budgets.conversion_label_account',
                            {account: accountName, interpolation: {escapeValue: false}})
                        : t('onboarding.budgets.conversion_label')}
                </span>
            </span>

            <span className="ob-conversion__control">
                <span className="ob-conversion__google">
                    <span className="ob-conversion__google-current">{currentLabelOf(chosen, actions, t)}</span>
                    <span className="ob-conversion__google-sep" aria-hidden="true">·</span>
                    <button type="button" className="ob-link" onClick={() => setOpen(true)}
                            aria-label={t('onboarding.budgets.google_change_aria',
                                          {account: accountName, interpolation: {escapeValue: false}})}>
                        {t('onboarding.budgets.google_change')}
                    </button>
                </span>
            </span>

            {open && (
                <Suspense fallback={null}>
                    <GoogleConversionModal entry={entry} accountName={accountName} chosen={chosen}
                                           actions={actions} loadAccountActions={loadAccountActions}
                                           onClose={() => setOpen(false)}
                                           onApply={actionIds => {
                                               dispatch({type: 'EDIT_CONVERSION', pk: entry.pk, actionIds});
                                               setOpen(false);
                                           }}/>
                </Suspense>
            )}
        </div>
    );
}

/**
 * Lo que la fila dice que se va a seguir: todas las primarias, o lo elegido.
 *
 * Sin número en «todas las primarias»: el listado de acciones no dice cuáles son primarias (contrato
 * §2.1), así que una «N» sería inventada. Lo elegido se nombra si las acciones de la cuenta ya se
 * trajeron en esta pestaña; si no (una recarga con el borrador guardado), se cuenta.
 */
export function currentLabelOf(chosen, actions, t) {
    if (!chosen.length) return t('onboarding.budgets.google_all_primary');
    const names = chosen.map(id => nameOf((actions?.actions || []).find(action => action.action_id === id)));
    if (names.every(Boolean)) return names.join(', ');
    return t('onboarding.budgets.google_chosen_count', {count: chosen.length});
}

function nameOf(action) {
    return action ? (action.name || action.description || null) : null;
}
